import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { AnimationPreviewComponent } from "../animations/AnimationPreviewComponent";
import { SelectionComponentSkinClass } from "./SelectionComponentSkinClass";
import { SelectionComponentSkinVariant } from "./SelectionComponentSkinVariant";
import { AnimationType } from "../../../shared/resources/AnimationAssetModel";
import { CharacterEditorSelectableItem } from "./CharacterEditorSelectableItem";
import { MdAccessibilityNew } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { FaCheckCircle } from "react-icons/fa";
import { ComponentTurnCharacter } from "./ComponentTurnCharacter";
import { undoableAnimationEditorSelectAnimationState } from "../../stores/undo/operation/AnimationEditorSelectionOp";
import { SelectionComponentAnimationTint } from "./SelectionComponentAnimationTint";
import { CharacterAnimationSkinColors, getImageNameByBodyType } from "../../canvas/game/character/characterAnimationHelper";
import { EditorSingleCharacterPropertiesGame } from "./EditorSingleCharacterPropertiesGame";
import { EditorSingleCharacterPropertiesEditor } from "./EditorSingleCharacterPropertiesEditor";
import { sharedStore } from "../../stores/SharedStore";
import { gameStore } from "../../stores/GameStore";
import { soundCache } from "../../stores/SoundCache";
import { UiSounds } from "../../canvas/game/sound/UiSounds";
import { staticImageAssetFolder } from "../../canvas/loader/StaticAssetLoader";
import { animationLoader } from "../../helper/AnimationLoader";
import { CharacterSelectionHelper } from "../../helper/CharacterSelectionHelper";
import { CharacterEditorChangeGroup, groupUndoableCharacterEditorChanges } from "../../stores/undo/operation/CharacterEditorSubmitCharacterConfigurationChangesOp";
import { CharacterEditorColorButton } from "../game/ui components/GameUIElements";
const AreaColumn = styled.div.withConfig({
  displayName: "EditorSingleCharacter__AreaColumn",
  componentId: "sc-5juy55-0"
})(["display:flex;flex-direction:column;flex-grow:0;padding:10px;"]);
const AreaColumnSpacer = styled.div.withConfig({
  displayName: "EditorSingleCharacter__AreaColumnSpacer",
  componentId: "sc-5juy55-1"
})(["display:flex;flex-grow:1;"]);
const AreaRow = styled.div.withConfig({
  displayName: "EditorSingleCharacter__AreaRow",
  componentId: "sc-5juy55-2"
})(["display:flex;flex-direction:row;flex-grow:1;padding:10px;"]);
const VerticalScrollAreaRow = styled(AreaRow).withConfig({
  displayName: "EditorSingleCharacter__VerticalScrollAreaRow",
  componentId: "sc-5juy55-3"
})(["flex-direction:row;overflow-x:hidden;overflow-y:auto;"]);
const FlexRowZero = styled.div.withConfig({
  displayName: "EditorSingleCharacter__FlexRowZero",
  componentId: "sc-5juy55-4"
})(["display:flex;flex-direction:row;flex-grow:0;"]);
const FlexRowOne = styled.div.withConfig({
  displayName: "EditorSingleCharacter__FlexRowOne",
  componentId: "sc-5juy55-5"
})(["display:flex;flex-direction:row;flex-grow:1;"]);
const FlexColumnZero = styled.div.withConfig({
  displayName: "EditorSingleCharacter__FlexColumnZero",
  componentId: "sc-5juy55-6"
})(["display:flex;flex-direction:column;flex-grow:0;"]);
const BodyShapeSelection = styled(FlexRowZero).withConfig({
  displayName: "EditorSingleCharacter__BodyShapeSelection",
  componentId: "sc-5juy55-7"
})(["max-width:444px;flex-wrap:wrap;"]);
const Item = styled(CharacterEditorSelectableItem).withConfig({
  displayName: "EditorSingleCharacter__Item",
  componentId: "sc-5juy55-8"
})(["width:100px;height:100px;text-align:center;align-items:center;justify-content:center;"]);
const BodyShapeImage = styled.img.withConfig({
  displayName: "EditorSingleCharacter__BodyShapeImage",
  componentId: "sc-5juy55-9"
})(["max-height:80px;"]);
const MissingItem = styled(CharacterEditorSelectableItem).withConfig({
  displayName: "EditorSingleCharacter__MissingItem",
  componentId: "sc-5juy55-10"
})(["width:100px;height:190px;color:red;cursor:not-allowed;"]);
const BodyPlaceholder = styled(MdAccessibilityNew).withConfig({
  displayName: "EditorSingleCharacter__BodyPlaceholder",
  componentId: "sc-5juy55-11"
})(["font-size:50px;text-align:center;flex-grow:1;"]);
const FinishButton = styled.div.withConfig({
  displayName: "EditorSingleCharacter__FinishButton",
  componentId: "sc-5juy55-12"
})(["float:right;display:flex;flex-direction:row;background-color:", ";height:40px;width:160px;color:black;cursor:pointer;border-radius:10px;align-content:center;justify-content:center;padding-top:4px;margin-bottom:10px;&.disabled{background-color:#3c3c3c;border-color:black;cursor:not-allowed;}"], CharacterEditorColorButton);
const CheckIcon = styled(FaCheckCircle).withConfig({
  displayName: "EditorSingleCharacter__CheckIcon",
  componentId: "sc-5juy55-13"
})(["margin-right:4px;"]);
export const EditorSingleCharacter = observer(props => {
  const {
    t
  } = useTranslation();
  const animationStore = props.store.animationSelectionStore;
  const playerNameIsNeededAndNotSet = props.isGame && !gameStore.playerName;
  const finishEnabled = !playerNameIsNeededAndNotSet;

  function getBodyShapeImageUrl(bodyShapeName) {
    const imageName = getImageNameByBodyType(bodyShapeName);
    return imageName != null ? staticImageAssetFolder + imageName + ".png" : null;
  }

  async function onClickSelectBodyShape(animationModel) {
    const {
      skins
    } = await animationLoader.loadAnimationDataCached(animationModel.id);
    groupUndoableCharacterEditorChanges(CharacterEditorChangeGroup.UnspecificGroupedChanges, () => {
      props.store.selectedCharacterConfiguration.setAnimationAssetName(animationModel.name);
      CharacterSelectionHelper.randomizeSkinSelection(skins, props.store.selectedCharacterConfiguration);
      animationStore.setSelectedSkinClass(null);
    });
  }

  function onColorSelectionChange(hexColor) {
    props.store.selectedCharacterConfiguration.setTintColorHex(hexColor);
  }

  function onClickFinish() {
    if (finishEnabled) {
      props.onFinish();
    }
  }

  useEffect(() => {
    const backgroundSound = props.isGame ? soundCache.playOneOf(UiSounds.CHAR_EDITOR_MUSIC, true) : null;
    return () => {
      backgroundSound === null || backgroundSound === void 0 ? void 0 : backgroundSound.stop();
    };
  }, []);
  const animationsBodyType = sharedStore.getAnimationsByType(AnimationType.BodyType);
  const animationsNPC = sharedStore.getAnimationsByType(AnimationType.NPC);
  const selectedAnimationName = props.store.selectedCharacterConfiguration.animationAssetName;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AreaColumn, null, props.isGame ? /*#__PURE__*/React.createElement(EditorSingleCharacterPropertiesGame, null) : /*#__PURE__*/React.createElement(EditorSingleCharacterPropertiesEditor, {
    store: props.store
  }), /*#__PURE__*/React.createElement(FlexColumnZero, null, /*#__PURE__*/React.createElement(FlexRowOne, null, t("editor.character_config_skin_color")), /*#__PURE__*/React.createElement(FlexRowOne, null, /*#__PURE__*/React.createElement(SelectionComponentAnimationTint, {
    hexColors: CharacterAnimationSkinColors,
    store: props.store,
    onColorSelectionChange: onColorSelectionChange
  }))), /*#__PURE__*/React.createElement(FlexColumnZero, null, t("editor.character_config_body_shape"), /*#__PURE__*/React.createElement(BodyShapeSelection, null, animationsBodyType.map(animationSnapshot => {
    var _animationStore$selec, _animationStore$selec2;

    return /*#__PURE__*/React.createElement(Item, {
      key: animationSnapshot.$modelId,
      className: ((_animationStore$selec = animationStore.selectedAnimation) === null || _animationStore$selec === void 0 ? void 0 : (_animationStore$selec2 = _animationStore$selec.animation) === null || _animationStore$selec2 === void 0 ? void 0 : _animationStore$selec2.name) == (animationSnapshot === null || animationSnapshot === void 0 ? void 0 : animationSnapshot.name) ? "selected" : "",
      onClick: async () => {
        await onClickSelectBodyShape(animationSnapshot);
      }
    }, getImageNameByBodyType(animationSnapshot === null || animationSnapshot === void 0 ? void 0 : animationSnapshot.name) != null && /*#__PURE__*/React.createElement(BodyShapeImage, {
      src: getBodyShapeImageUrl(animationSnapshot === null || animationSnapshot === void 0 ? void 0 : animationSnapshot.name)
    }), getImageNameByBodyType(animationSnapshot === null || animationSnapshot === void 0 ? void 0 : animationSnapshot.name) == null && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BodyPlaceholder, null), animationSnapshot === null || animationSnapshot === void 0 ? void 0 : animationSnapshot.localizedName.get(gameStore.languageKey)));
  }), props.store.hasMissingAnimationReference && /*#__PURE__*/React.createElement(MissingItem, {
    className: !animationStore.selectedAnimation ? "selected" : ""
  }, /*#__PURE__*/React.createElement(BodyPlaceholder, null), t("editor.character_config_reference_animation_missing"), props.store.selectedCharacterConfiguration.animationAssetName))), !props.isGame && /*#__PURE__*/React.createElement(FlexColumnZero, null, /*#__PURE__*/React.createElement("select", {
    value: selectedAnimationName,
    onChange: ({
      target
    }) => onClickSelectBodyShape(animationsNPC.find(animation => animation.name === target.value))
  }, animationsNPC.map(animation => /*#__PURE__*/React.createElement("option", {
    key: animation.name,
    value: animation.name
  }, animation === null || animation === void 0 ? void 0 : animation.name)), animationsNPC.every(animation => animation.name !== selectedAnimationName) && animationsBodyType.some(animation => animation.name === selectedAnimationName) && /*#__PURE__*/React.createElement("option", {
    value: selectedAnimationName
  }, sharedStore.getAnimationByName(selectedAnimationName).localizedName.get(gameStore.languageKey)), props.store.hasMissingAnimationReference && /*#__PURE__*/React.createElement("option", {
    value: props.store.selectedCharacterConfiguration.animationAssetName,
    disabled: true
  }, t("editor.character_config_reference_animation_missing"), " ", props.store.selectedCharacterConfiguration.animationAssetName)))), /*#__PURE__*/React.createElement(AreaColumn, null, /*#__PURE__*/React.createElement(FlexRowOne, null, /*#__PURE__*/React.createElement(AnimationPreviewComponent, {
    store: animationStore,
    editMode: false
  })), /*#__PURE__*/React.createElement(FlexRowOne, null, /*#__PURE__*/React.createElement(ComponentTurnCharacter, {
    onAnimationStateChange: name => {
      undoableAnimationEditorSelectAnimationState(name, animationStore);
    }
  }))), /*#__PURE__*/React.createElement(AreaColumnSpacer, null), /*#__PURE__*/React.createElement(AreaColumn, null, /*#__PURE__*/React.createElement(VerticalScrollAreaRow, null, props.store.animationSelectionStore.selectedAnimation && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SelectionComponentSkinClass, {
    store: props.store
  }), /*#__PURE__*/React.createElement(SelectionComponentSkinVariant, {
    isShown: props.store.animationSelectionStore.selectedSkinClass != null,
    store: props.store
  }))), props.isGame && /*#__PURE__*/React.createElement(FinishButton, {
    className: finishEnabled ? "" : "disabled",
    onClick: onClickFinish
  }, /*#__PURE__*/React.createElement(CheckIcon, null), t(props.finishButtonTranslationKey))));
});